/* Cease custom behaviour: popups (video, newsletter) and the product size dropdown. */

(() => {
  const SUBSCRIBED_KEY = 'cease:newsletter-subscribed';

  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {}
    },
  };

  /* ---------- Modals (native <dialog>) ---------- */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // [data-zoom] dialogs scale out of / back into the element that opened them, like a macOS window
  function zoomKeyframes(dialog) {
    const panel = dialog.querySelector('.cease-modal__panel');
    const origin = dialog.zoomOrigin;
    if (!origin || !origin.isConnected) return null;

    const from = origin.getBoundingClientRect();
    const to = panel.getBoundingClientRect();
    if (!from.width || !to.width) return null;

    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    const scale = Math.max(from.width / to.width, 0.02);

    // Scale runs the whole duration; the fade only covers the first/last part so the icon "grows" into the video
    const collapsed = { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 };
    const expanded = { transform: 'none', opacity: 1 };
    return { collapsed, expanded };
  }

  function animateZoom(dialog, direction) {
    if (!dialog.hasAttribute('data-zoom') || reduceMotion.matches || !dialog.animate) return Promise.resolve();

    const keyframes = zoomKeyframes(dialog);
    const opening = direction === 'in';
    const options = opening
      ? { duration: 480, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      : { duration: 320, easing: 'cubic-bezier(0.7, 0, 0.84, 0)' };
    const animations = [];

    if (keyframes) {
      const panel = dialog.querySelector('.cease-modal__panel');
      const { collapsed, expanded } = keyframes;
      const frames = opening
        ? [collapsed, { opacity: 1, offset: 0.3 }, expanded]
        : [expanded, { opacity: 1, offset: 0.7 }, collapsed];
      animations.push(panel.animate(frames, { ...options, fill: 'both' }));
    }
    try {
      animations.push(
        dialog.animate({ opacity: opening ? [0, 1] : [1, 0] }, { ...options, pseudoElement: '::backdrop', fill: 'both' })
      );
    } catch (e) {}

    return Promise.all(animations.map((animation) => animation.finished)).then(
      () => animations.forEach((animation) => animation.cancel()),
      () => {}
    );
  }

  function openModal(dialog, origin) {
    if (!dialog || dialog.open) return;
    dialog.zoomOrigin = origin;
    dialog.showModal();
    document.body.classList.add('cease-modal-open');
    dialog.dispatchEvent(new CustomEvent('cease:open'));
    animateZoom(dialog, 'in');
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.open || dialog.closing) return;
    dialog.closing = true;
    dialog.dispatchEvent(new CustomEvent('cease:closing'));
    animateZoom(dialog, 'out').then(() => {
      dialog.closing = false;
      dialog.close();
    });
  }

  document.querySelectorAll('dialog.cease-modal').forEach((dialog) => {
    dialog.addEventListener('close', () => {
      document.body.classList.remove('cease-modal-open');
      dialog.dispatchEvent(new CustomEvent('cease:close'));
    });

    // Escape: run the closing animation instead of closing instantly
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal(dialog);
    });

    // Click on the backdrop (the dialog element itself, outside the panel) closes it
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-modal-close]')) closeModal(dialog);
    });
  });

  /* ---------- Video ---------- */

  const videoModal = document.getElementById('VideoModal');
  if (videoModal) {
    const video = videoModal.querySelector('video');

    videoModal.addEventListener('cease:open', () => {
      video.currentTime = 0;
      const playing = video.play();
      if (playing) playing.catch(() => {});
    });
    videoModal.addEventListener('cease:closing', () => video.pause());
    videoModal.addEventListener('cease:close', () => video.pause());
    video.addEventListener('ended', () => closeModal(videoModal));
  }

  /* ---------- Newsletter ---------- */

  const newsletterModal = document.getElementById('NewsletterModal');
  if (newsletterModal) {
    const form = newsletterModal.querySelector('form');
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"]');
    const status = form.querySelector('.cease-newsletter__status');
    const { klaviyoKey, klaviyoList } = newsletterModal.dataset;

    const markSubscribed = () => {
      storage.set(SUBSCRIBED_KEY, '1');
      form.classList.add('is-subscribed');
      button.disabled = true;
      input.disabled = true;
      button.setAttribute('aria-label', 'Subscribed');
    };

    if (storage.get(SUBSCRIBED_KEY)) markSubscribed();

    const subscribeWithKlaviyo = (email) =>
      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(klaviyoKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Website newsletter popup',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: klaviyoList } } },
          },
        }),
      }).then((response) => {
        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
      });

    // Fallback until a Klaviyo list is configured: Shopify's own customer newsletter form
    const subscribeWithShopify = () =>
      fetch(form.action, { method: 'POST', body: new FormData(form) }).then((response) => {
        if (!response.ok || /challenge/.test(response.url)) throw new Error('Shopify signup failed');
      });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (form.classList.contains('is-subscribed')) return;
      if (!input.checkValidity()) {
        status.textContent = 'Please enter a valid email.';
        input.focus();
        return;
      }

      button.disabled = true;
      status.textContent = '';
      const email = input.value.trim();
      const request = klaviyoKey && klaviyoList ? subscribeWithKlaviyo(email) : subscribeWithShopify();

      request.then(markSubscribed).catch(() => {
        button.disabled = false;
        status.textContent = 'Something went wrong, please try again.';
      });
    });
  }

  /* ---------- Triggers ---------- */

  document.addEventListener('click', (event) => {
    const videoTrigger = event.target.closest('[data-video-open]');
    if (videoTrigger) {
      event.preventDefault();
      openModal(videoModal, videoTrigger);
      return;
    }

    const newsletterTrigger = event.target.closest('[data-newsletter-open], a[href$="#newsletter"]');
    if (newsletterTrigger && newsletterModal) {
      event.preventDefault();
      openModal(newsletterModal);
    }
  });

  // Allow linking straight to the popup, e.g. ceaseceasecease.com/#newsletter
  if (window.location.hash === '#newsletter') openModal(newsletterModal);
})();

/* ---------- Product size dropdown ---------- */
/* Wraps the size radio inputs so <variant-radios> keeps handling variant changes. */

class SizeDropdown extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.size-dropdown__toggle');
    this.list = this.querySelector('.size-dropdown__list');
    this.label = this.querySelector('.size-dropdown__label');

    this.toggle.addEventListener('click', () => this.setOpen(this.list.hidden));
    this.list.addEventListener('change', (event) => {
      this.label.textContent = event.target.value;
    });
    // Close on any pick, including re-picking the current size (which fires no change event)
    this.list.addEventListener('click', (event) => {
      if (!event.target.closest('label')) return;
      setTimeout(() => {
        this.setOpen(false);
        this.toggle.focus();
      });
    });

    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.list.hidden) {
        this.setOpen(false);
        this.toggle.focus();
      }
    };
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  setOpen(open) {
    this.list.hidden = !open;
    this.toggle.setAttribute('aria-expanded', open);
    this.classList.toggle('is-open', open);
    if (open) {
      const checked = this.list.querySelector('input:checked') || this.list.querySelector('input');
      if (checked) checked.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('size-dropdown')) customElements.define('size-dropdown', SizeDropdown);
